import { useReducer, useEffect } from 'react'
import api from '@api'
import { Order, IPageInfo, INeighborhoodTableRecord, IEmployee } from '@api/types'
import { uniqueId } from 'lodash'
import { useSelection } from '@hooks'
import { ISelection } from '@hooks/useSelection'
import { useShouldReload, useReload } from '@hooks/reloadContext'
import { INeighborhoodVariables } from '@api/neighborhood/list'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  NEXT_PAGE,
  SET_QUERY,
  ERROR,
  OPTIMISTIC_DELETE,
  CLEAR_FILTERS,
  SET_LOCATIONS,
  SET_CREATED_BY,
  APPLY_FILTERS,
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}

interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  neighborhoods: INeighborhoodTableRecord[]
  count: number
  pageInfo: IPageInfo
}

interface SortAction {
  type: Actions.SORT
  column: string
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface QueryFilterAction {
  type: Actions.SET_QUERY
  query: string
}

interface ErrorAction {
  type: Actions.ERROR
  reqId: string
}
interface OptimisticDeleteAction {
  type: Actions.OPTIMISTIC_DELETE
  ids: string[]
}

interface ClearFiltersAction {
  type: Actions.CLEAR_FILTERS
}

interface LocationsFilterAction {
  type: Actions.SET_LOCATIONS
  locations: any[]
}

interface CreatedByFilterAction {
  type: Actions.SET_CREATED_BY
  createdBy: IEmployee[]
}

interface ApplyFiltersAction {
  type: Actions.APPLY_FILTERS
  filters: NHFilters
}

export interface IDHash {
  [id: string]: boolean
}

type Action =
  | LoadAction
  | LoadStartAction
  | NextPageAction
  | SortAction
  | QueryFilterAction
  | ErrorAction
  | LoadStartActionWithoutSkeleton
  | OptimisticDeleteAction
  | ClearFiltersAction
  | LocationsFilterAction
  | CreatedByFilterAction
  | ApplyFiltersAction

interface Sort {
  column: string
  order: Order
}

interface State {
  reqId: string
  isLoading: boolean
  isSorting: boolean
  isPaging: boolean
  count: number
  items: INeighborhoodTableRecord[]
  after: string | null
  pageInfo: IPageInfo
  sort: Sort
  first: number
  filters: NHFilters
}

export interface NHFilters {
  query: string
  locations: any[]
  createdBy: IEmployee[]
  nhCapacityMin: null | number
  nhCapacityMax: null | number
  sharingRatioMin: null | number
  sharingRatioMax: null | number
  numberOfSeatsMin: null | number
  numberOfSeatsMax: null | number
  numberOfEmployeesMin: null | number
  numberOfEmployeesMax: null | number
}

export interface UseNeighborhoodsHook extends State {
  sortBy: (column: string) => void
  nextPage: () => void
  applyQuery: (query: string) => void
  clearQuery: () => void
  reload: () => void
  selection: ISelection
  optimisticDelete: (ids: string[]) => void
  clearFilters: () => void
  filterCount: () => number
  setFilteredLocations: (locations: any[]) => void
  setCreatedBy: (createdBy: IEmployee[]) => void
  applyFilters: (filters: NHFilters) => void
}

let persistedState: State = {
  reqId: uniqueId('request'),
  isLoading: true,
  isSorting: false,
  isPaging: false,
  count: 0,
  first: PAGE_SIZE,
  items: [],
  after: null,
  sort: { column: 'name', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  filters: {
    query: '',
    locations: [],
    createdBy: [],
    nhCapacityMin: null,
    nhCapacityMax: null,
    sharingRatioMin: null,
    sharingRatioMax: null,
    numberOfSeatsMin: null,
    numberOfSeatsMax: null,
    numberOfEmployeesMin: null,
    numberOfEmployeesMax: null,
  },
}

interface GetVariablesFunc {
  filters: NHFilters
  sort: Sort
  after?: string | null
  first?: number
}

function getVariables({ filters, sort, after, first }: GetVariablesFunc): INeighborhoodVariables {
  const filterList = []

  if (filters.locations.length) {
    filterList.push({
      floorId_in: filters.locations.map(({ id }) => id),
    })
  }

  if (filters.query.length) {
    filterList.push({
      name_contains: filters.query,
    })
  }

  if (filters.createdBy.length) {
    filterList.push({
      createdByEmployeeId_in: filters.createdBy.map(({ id }) => id),
    })
  }

  const where = {
    AND: filterList,
  }

  return {
    after,
    orderBy: sort.column + '_' + sort.order,
    first,
    ...(where.AND.length
      ? {
          where,
        }
      : {}),
  }
}

export default function useNeighborhoods(): UseNeighborhoodsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const selection = useSelection()
  const shouldReload = useShouldReload()
  const reload = useReload()

  const fetchNeighborhoods =
    ({
      showSkeleton,
      loadAll,
      shouldClearSelection = true,
    }: {
      showSkeleton: boolean
      loadAll: boolean
      shouldClearSelection?: boolean
    }) =>
    () => {
      const reqId = uniqueId('request')
      const variables = getVariables({
        after: loadAll ? null : state.after,
        sort: state.sort,
        first: loadAll ? state.first : PAGE_SIZE,
        filters: state.filters,
      })

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      api.neighborhood.list(variables).then(({ edges, counters, pageInfo }) => {
        const isAllItemsSelected = selection.hasAllItemsSelected(state.items.length)
        const itemIds: string[] = []

        const items = edges.map(({ node }) => {
          if (isAllItemsSelected) {
            itemIds.push(node.id)
          }

          return node
        })

        //clear selection outside filtered range
        if (shouldClearSelection) {
          selection.removeSelectionsByItems(items)
        }

        // select all items if the select all is checked
        if (isAllItemsSelected && items.length) {
          selection.addAll(itemIds)
        }

        dispatch({
          type: Actions.LOAD,
          count: counters.total,
          neighborhoods: items,
          pageInfo,
          reqId,
        })
      })
    }

  //api request without skeleton and with load only 25 items after cursor
  useEffect(
    fetchNeighborhoods({ showSkeleton: false, loadAll: false, shouldClearSelection: false }),
    [state.after]
  )

  //api request without skeleton and with load all items in state.first
  useEffect(fetchNeighborhoods({ showSkeleton: false, loadAll: true }), [
    state.sort,
    shouldReload.neighborhoods,
  ])

  //api request with skeleton and with load all items in state.first
  useEffect(fetchNeighborhoods({ showSkeleton: true, loadAll: true }), [state.filters])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  return {
    ...state,
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
    },
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    applyQuery: (query: string) => {
      dispatch({ type: Actions.SET_QUERY, query })
    },
    optimisticDelete: (ids: string[]) => {
      dispatch({ type: Actions.OPTIMISTIC_DELETE, ids })
    },
    clearQuery: () => {
      dispatch({
        type: Actions.SET_QUERY,
        query: '',
      })
    },
    clearFilters: () => {
      dispatch({
        type: Actions.CLEAR_FILTERS,
      })
    },
    reload: () => reload.neighborhoods(),
    selection: selection,
    filterCount: () => {
      let count = 0

      if (state.filters.query) {
        count++
      }

      if (state.filters.locations.length) {
        count++
      }

      if (state.filters.createdBy.length) {
        count++
      }

      if (state.filters.nhCapacityMin) {
        count++
      }

      if (state.filters.nhCapacityMax) {
        count++
      }

      if (state.filters.numberOfEmployeesMin) {
        count++
      }

      if (state.filters.numberOfEmployeesMax) {
        count++
      }

      if (state.filters.numberOfSeatsMin) {
        count++
      }

      if (state.filters.numberOfSeatsMax) {
        count++
      }

      if (state.filters.sharingRatioMin) {
        count++
      }

      if (state.filters.sharingRatioMax) {
        count++
      }

      return count
    },
    setFilteredLocations: (locations: any[]) => {
      dispatch({
        type: Actions.SET_LOCATIONS,
        locations,
      })
    },
    setCreatedBy: (createdBy: IEmployee[]) => {
      dispatch({
        type: Actions.SET_CREATED_BY,
        createdBy,
      })
    },
    applyFilters: (filters: NHFilters) => {
      dispatch({
        type: Actions.APPLY_FILTERS,
        filters,
      })
    },
  }

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case Actions.LOAD_START:
        return {
          ...state,
          reqId: action.reqId,
          isLoading: true,
        }
      case Actions.LOAD_START_WITHOUT_SKELETON:
        return {
          ...state,
          reqId: action.reqId,
          isLoading: false,
        }

      case Actions.ERROR:
        return action.reqId === state.reqId
          ? { ...state, isLoading: false, isSorting: false, isPaging: false }
          : state

      case Actions.LOAD:
        return action.reqId === state.reqId
          ? {
              ...state,
              isLoading: false,
              isSorting: false,
              isPaging: false,
              count: action.count,
              items: state.isPaging
                ? [...state.items, ...action.neighborhoods]
                : action.neighborhoods,
              pageInfo: action.pageInfo,
            }
          : state

      case Actions.SORT:
        return {
          ...state,
          isSorting: true,
          pageInfo: { hasNextPage: false, endCursor: '' },
          sort: {
            column: action.column,
            order:
              state.sort.column === action.column
                ? state.sort.order === Order.ASC
                  ? Order.DESC
                  : Order.ASC
                : Order.ASC,
          },
        }

      case Actions.NEXT_PAGE:
        return {
          ...state,
          after: state.pageInfo.endCursor,
          isPaging: true,
          first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
        }

      case Actions.SET_QUERY:
        return {
          ...state,
          filters: {
            ...state.filters,
            query: action.query,
          },
          pageInfo: { hasNextPage: false, endCursor: '' },
        }

      case Actions.OPTIMISTIC_DELETE:
        return {
          ...state,
          items: state.items.filter((nh) => !action.ids.includes(nh.id)),
        }

      case Actions.CLEAR_FILTERS:
        return {
          ...state,
          filters: {
            query: '',
            locations: [],
            createdBy: [],
            nhCapacityMin: null,
            nhCapacityMax: null,
            sharingRatioMin: null,
            sharingRatioMax: null,
            numberOfSeatsMin: null,
            numberOfSeatsMax: null,
            numberOfEmployeesMin: null,
            numberOfEmployeesMax: null,
          },
        }

      case Actions.SET_LOCATIONS:
        return {
          ...state,
          filters: {
            ...state.filters,
            locations: action.locations,
          },
          pageInfo: { hasNextPage: false, endCursor: '' },
        }

      case Actions.SET_CREATED_BY:
        return {
          ...state,
          filters: {
            ...state.filters,
            createdBy: action.createdBy,
          },
          pageInfo: { hasNextPage: false, endCursor: '' },
        }

      case Actions.APPLY_FILTERS:
        return {
          ...state,
          filters: action.filters,
          pageInfo: { hasNextPage: false, endCursor: '' },
        }

      default:
        return state
    }
  }
}
